import type { PageTargeting } from "../../../config/page";
import type { AuctionData } from "../../../core/AdEnginePlugin";
import { PrebidBidderAdapter } from "./PrebidBidderAdapter";

/**
 * Bidder adapter for Rubicon to modify the `bids.params` and add `keywords` and
 * `inventory` fields.
 *
 * - `bids.params.keywords` is set to `["rp.fastlane"]`
 * - `bids.params.inventory` is extended with all the `pageTargeting` data from
 * the auction.
 */
export class RubiconBidderAdapter extends PrebidBidderAdapter<"rubicon"> {
  transformAdUnitBidConfigs(
    bidderConfigs: PrebidAdUnitBidForKey["rubicon"][],
    auctionData: AuctionData,
  ): PrebidAdUnitBidForKey["rubicon"][] {
    for (const bid of bidderConfigs) {
      bid.params.keywords = ["rp.fastlane"];
      bid.params.inventory = {
        ...bid.params.inventory,
        ...this.formatPageTargetingForRubicon(auctionData.pageTargeting),
      };
    }

    return bidderConfigs;
  }

  /**
   * Converts the page targeting from an object to array based format rubicon expects.
   *
   * ```
   * { key: value } -> [[key, [value]]
   * ```
   * @param pageTargeting The page targeting to format for Rubicon.
   * @returns The targeting converted from an object to array of arrays
   */
  private formatPageTargetingForRubicon(
    initialPageTargeting: PageTargeting,
  ): Record<string, string[]> {
    return Object.fromEntries(
      Object.entries(initialPageTargeting).map(([key, value]) => [
        key,
        value ? [String(value)] : [],
      ]),
    );
  }
}
